import React, { useEffect, useState } from "react";
import { api, fmtDate, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Phone } from "lucide-react";
import { SearchBox, DeleteButton, matchesQuery } from "@/components/crm/RecordActions";
import FilterBar, { inDateRange } from "@/components/crm/Filters";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const empty = { name:"", mobile:"", whatsapp:"", email:"", address:"", city:"", discom:"", consumer_number:"", solar_capacity:"", panel_details:"", inverter:"", installation_date:"", warranty:"" };

export default function Customers() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState("");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");

  const load = async () => setItems((await api.get("/customers")).data);
  useEffect(()=>{ load(); },[]);

  const save = async () => {
    try {
      if (editId) await api.patch(`/customers/${editId}`, form);
      else await api.post("/customers", form);
      toast.success("Saved");
      setOpen(false); setForm(empty); setEditId(null); load();
    } catch(e){ toast.error(formatError(e.response?.data?.detail)); }
  };

  const edit = (c) => { setForm({...empty, ...c}); setEditId(c.id); setOpen(true); };

  const del = async (c) => {
    try { await api.delete(`/customers/${c.id}`); toast.success(`Customer ${c.customer_no} deleted`); load(); }
    catch(e){ toast.error(formatError(e.response?.data?.detail)); }
  };

  const visible = items
    .filter((c) => matchesQuery(c, search))
    .filter((c) => inDateRange(c.created_at, fFrom, fTo));

  return (
    <div className="space-y-5" data-testid="customers-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900" style={{fontFamily:'Outfit'}}>Customers <span className="text-slate-400 text-lg font-medium">/ ग्राहक</span></h1>
          <p className="text-slate-500 text-sm">Master directory of every solar installation.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <SearchBox value={search} onChange={setSearch} placeholder="Search name, mobile, city..." testid="customers-search-input" />
          <Dialog open={open} onOpenChange={(v)=>{setOpen(v); if(!v){setForm(empty); setEditId(null);}}}>
            <DialogTrigger asChild>
              <Button className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="add-customer-btn"><Plus className="w-4 h-4 mr-1"/>New Customer</Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{editId?"Edit":"New"} Customer</DialogTitle></DialogHeader>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  ["name","Name*"],["mobile","Mobile*"],["whatsapp","WhatsApp"],["email","Email"],
                  ["city","City"],["address","Address"],["discom","DISCOM"],["consumer_number","Consumer Number"],
                  ["solar_capacity","Solar Capacity (kW)"],["panel_details","Panel Details"],["inverter","Inverter"],
                  ["installation_date","Installation Date"],["warranty","Warranty"],
                ].map(([k,l])=>(
                  <div key={k}>
                    <Label className="text-xs text-slate-600 font-semibold">{l}</Label>
                    <Input type={k==="installation_date"?"date":"text"} value={form[k]||""} onChange={e=>setForm({...form,[k]:e.target.value})} data-testid={`customer-${k}-input`} className="mt-1"/>
                  </div>
                ))}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
                <Button className="bg-amber-500 hover:bg-amber-600" onClick={save} data-testid="save-customer-btn">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <FilterBar from={fFrom} to={fTo} onFrom={setFFrom} onTo={setFTo} onClear={()=>{setFFrom("");setFTo("");setSearch("");}} />

      <Card><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Cust #</TableHead><TableHead>Name</TableHead><TableHead>Mobile</TableHead>
            <TableHead>City</TableHead><TableHead>Capacity</TableHead><TableHead>DISCOM</TableHead>
            <TableHead>Installed</TableHead><TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {visible.length===0 && <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-400">{search ? `No customers matching "${search}"` : "No customers yet. Convert a lead or add manually."}</TableCell></TableRow>}
            {visible.map(c=>(
              <TableRow key={c.id} data-testid={`customer-row-${c.customer_no}`}>
                <TableCell className="font-mono text-xs">{c.customer_no}</TableCell>
                <TableCell className="font-medium">{c.name}</TableCell>
                <TableCell><a href={`tel:${c.mobile}`} className="inline-flex items-center gap-1 text-slate-700 hover:text-amber-600"><Phone className="w-3 h-3"/>{c.mobile}</a></TableCell>
                <TableCell className="text-sm">{c.city||"-"}</TableCell>
                <TableCell className="text-sm">{c.solar_capacity||"-"}</TableCell>
                <TableCell className="text-sm">{c.discom||"-"}</TableCell>
                <TableCell className="text-xs">{fmtDate(c.installation_date)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" onClick={()=>edit(c)} data-testid={`edit-customer-${c.customer_no}`}>Edit</Button>
                    {user?.role === "admin" && (
                      <DeleteButton title={`Delete Customer ${c.customer_no}?`} description={`This will permanently delete "${c.name}" and their record. This action cannot be undone.`} onConfirm={()=>del(c)} testid={`delete-customer-${c.customer_no}`} />
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}
